import { useEffect, useState } from "react";
import { api } from "./api";
import { Login } from "./Login";
import { Projects } from "./Projects";
import { ProjectPage } from "./ProjectPage";
import { Viewer } from "./Viewer";
import { Compare } from "./Compare";
import { useRoute } from "./router";
import { DailyReports } from "./DailyReports";
import { DailyReportForm } from "./DailyReportForm";
import { Users } from "./Users";
import { Profile } from "./Profile";
import { CreatePassword } from "./Password";
import { SessionContext, type Me } from "./session";
import { setActorName } from "./useDocSync";
import { clearOfflineData, rememberedMe, rememberMe } from "./offline";

export function App() {
  const [auth, setAuth] = useState<"checking" | "in" | "out">("checking");
  const [me, setMe] = useState<Me | null>(null);
  const route = useRoute();
  const signedIn = (u: Me) => { rememberMe(u); setMe(u); setActorName(u.displayName); setAuth("in"); };

  useEffect(() => {
    api.me().then((r) => { if (r.authenticated && r.user) signedIn(r.user); else setAuth("out"); })
      // No signal is not "signed out": keep the last signed-in user on this device until the server says otherwise.
      .catch(() => { const u = rememberedMe<Me>(); if (u) signedIn(u); else setAuth("out"); });
    const onUnauthorized = () => { setMe(null); setAuth("out"); };
    const onMustChange = () => setMe((m) => (m ? { ...m, mustChangePassword: true } : m));
    window.addEventListener("cl:unauthorized", onUnauthorized);
    window.addEventListener("cl:mustchangepassword", onMustChange);
    return () => { window.removeEventListener("cl:unauthorized", onUnauthorized); window.removeEventListener("cl:mustchangepassword", onMustChange); };
  }, []);

  if (auth === "checking") return <div className="center muted">Loading…</div>;
  if (auth === "out") return <Login onSignedIn={signedIn} />;

  const signOut = () => api.logout().catch(() => {}).then(() => clearOfflineData()).finally(() => { setMe(null); setAuth("out"); });
  // Starting/temporary password: this screen only, until they make their own (admins too).
  if (me?.mustChangePassword) return <CreatePassword me={me} onSignOut={signOut} onDone={(u) => { signedIn(u); location.hash = "#/"; }} />;
  const page = (() => {
    switch (route.name) {
      case "projects": return <Projects onSignOut={signOut} />;
      case "project": return <ProjectPage key={route.id} id={route.id} tab={route.tab} onSignOut={signOut} />;
      case "reports": return <DailyReports query={route.query} onSignOut={signOut} />;
      case "report": return <DailyReportForm key={route.id} id={route.id} onSignOut={signOut} />;
      case "newReport": return <DailyReportForm key="new" projectId={route.projectId} onSignOut={signOut} />;
      case "users": return <Users onSignOut={signOut} />;
      case "profile": return <Profile onSignOut={signOut} />;
      case "compare": return <Compare n={route.n} np={route.np} o={route.o} op={route.op} mode={route.mode} />;
      case "viewer": return <Viewer key={route.id} id={route.id} initialPage={route.page} focusPin={route.pin} focusSel={route.sel} place={route.place} />;
    }
  })();
  return <SessionContext.Provider value={me}>{page}</SessionContext.Provider>;
}
